import { useState, useRef, useEffect } from 'react';
import { Send, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import type { Entry, Participant } from '../lib/types';

interface LetterGridProps {
  sessionId: string;
  participant: Participant | null;
  entries: Entry[];
  participants: Participant[];
  onNewEntry: (entry: Entry) => void;
  onInputFocus: () => void;
  onInputBlur: () => void;
  isViewOnly?: boolean;
}

export function LetterGrid({
  sessionId,
  participant,
  entries,
  participants,
  onNewEntry,
  onInputFocus,
  onInputBlur,
  isViewOnly = false
}: LetterGridProps) {
  const { t } = useTranslation();
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [participantNicknames, setParticipantNicknames] = useState<Record<string, string>>({});
  
  // Update nicknames whenever participants change
  useEffect(() => {
    const newNicknames = participants.reduce((acc, p) => ({
      ...acc,
      [p.id]: p.nickname
    }), {...participantNicknames});
    
    setParticipantNicknames(newNicknames);
  }, [participants]);
  
  useEffect(() => {
    // Auto-focus input on component mount if not in view-only mode
    if (!isViewOnly) {
      inputRef.current?.focus();
    }
  }, [isViewOnly]);

  useEffect(() => {
    const loadParticipantNicknames = async () => {
      try {
        const { data: historicalParticipants, error } = await supabase
          .from('entries')
          .select('participant_id, participants(nickname)')
          .eq('session_id', sessionId)
          .not('participant_id', 'in', `(${participants.map(p => p.id).join(',')})`);

        if (error) throw error;

        const nicknames = (historicalParticipants || []).reduce((acc, entry) => {
          if (entry.participants?.nickname) {
            return {
              ...acc,
              [entry.participant_id]: entry.participants.nickname
            };
          }
          return acc;
        }, {});

        setParticipantNicknames(nicknames);
      } catch (error) {
        console.error('Error loading participant nicknames:', error);
      }
    };

    loadParticipantNicknames();
  }, [sessionId, participants]);

  const entriesByLetter = entries.reduce((acc, entry) => {
    const letter = entry.content.trim().charAt(0).toUpperCase();
    if (!acc[letter]) acc[letter] = [];
    acc[letter].push(entry);
    return acc;
  }, {} as Record<string, Entry[]>);

  // Helper function to get participant nickname
  const getParticipantNickname = (participantId: string) => {
    const entryParticipant = participants.find(p => p.id === participantId);
    if (entryParticipant) {
      return entryParticipant.nickname;
    }

    // Check historical nicknames
    const historicalNickname = participantNicknames[participantId];
    return historicalNickname || t('letter_grid.participant.former');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim() || isSubmitting || isViewOnly || !participant) return;

    // Check for duplicate content
    const trimmedContent = content.trim();
    const isDuplicate = entries.some(entry => 
      entry.content.toLowerCase() === trimmedContent.toLowerCase()
    );

    if (isDuplicate) {
      setError(t('letter_grid.error.duplicate'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const firstLetter = trimmedContent.charAt(0).toUpperCase();
      const { data, error } = await supabase
        .from('entries')
        .insert({
          session_id: sessionId,
          participant_id: participant.id,
          letter: firstLetter,
          content: trimmedContent
        })
        .select()
        .single();

      if (error) throw error;
      if (data) {
        onNewEntry(data);
        setContent('');
        inputRef.current?.focus();
      }
    } catch (error) {
      console.error('Error adding entry:', error);
      setError(t('letter_grid.error.save_failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {!isViewOnly && (
        <form onSubmit={handleSubmit} className="sticky top-0 z-10 -mt-6 bg-gradient-to-b from-primary-50/95 to-accent-50/95 backdrop-blur-sm p-4 sm:p-6 rounded-lg shadow-sm mb-6">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onFocus={onInputFocus}
              onBlur={onInputBlur}
              placeholder={t('letter_grid.input.placeholder')}
              className="flex-1 rounded-lg border border-gray-300 bg-white/80 px-3 sm:px-4 py-2 sm:py-3 text-base sm:text-lg focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 placeholder:text-gray-400"
              autoComplete="off"
            />
            <button
              type="submit"
              disabled={!content.trim() || isSubmitting}
              className="rounded-lg bg-primary-500 px-4 sm:px-5 py-2 sm:py-3 text-white transition-colors hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="h-5 w-5" />
            </button>
          </div>
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600">
              <AlertCircle className="h-4 w-4" />
              <span>{error}</span>
            </div>
          )}
        </form>
      )}

      <div className="space-y-4">
        {Object.entries(entriesByLetter).sort().map(([letter, letterEntries]) => (
          <div key={letter} className="rounded-lg bg-white p-4 shadow-sm">
            <h3 className="mb-2 font-semibold text-gray-900">
              {t('letter_grid.letter')} {letter} ({letterEntries.length})
            </h3>
            <div className="space-y-2">
              {letterEntries.map((entry) => (
                <div
                  key={entry.id}
                  className={`rounded-lg p-3 ${
                    !isViewOnly && entry.participant_id === participant?.id
                      ? 'bg-primary-50 text-primary-900'
                      : 'bg-gray-50 text-gray-900'
                  }`}
                >
                  <p className="break-words">{entry.content}</p>
                  {!isViewOnly && (
                    <p className={`mt-1 text-sm ${
                      entry.participant_id === participant?.id ? 'text-primary-600' : 'text-gray-500'
                    }`}>
                      {t('letter_grid.entry.by')} {entry.participant_id === participant?.id ? t('letter_grid.entry.you') : getParticipantNickname(entry.participant_id)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}